"use client"

import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "./ui/button"
import { Card, CardContent } from "./ui/card"
import { Star, ArrowLeft } from "lucide-react"
import { onValue, ref } from '../lib/firebase-database'
import { db } from '../firebase'

export default function ReviewsList() {
  const location = useLocation()
  const navigate = useNavigate()
  const { reviews = [], villaName = "", isOwner = false, isAdmin = false } = location.state || {}
  const [sortBy, setSortBy] = useState("newest")
  const [expandedReviews, setExpandedReviews] = useState({})
  const [userPhotos, setUserPhotos] = useState({})
  const [userNames, setUserNames] = useState({});

  useEffect(() => {
    const usersRef = ref(db, 'users')
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val()
      if (users) {
        const photos = {}
        Object.entries(users).forEach(([userId, userData]) => {
          if (userData.photoURL) {
            photos[userId] = userData.photoURL
          }
        })
        setUserPhotos(photos)
      }
    })

    return () => unsubscribe()
  }, [])

  useEffect(() => {
    const usersRef = ref(db, 'users');
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const users = snapshot.val();
      if (users) {
        const names = {};
        Object.entries(users).forEach(([userId, userData]) => {
          if (userData.displayName) {
            names[userId] = userData.displayName;
          }
        });
        setUserNames(names);
      }
    });

    return () => unsubscribe();
  }, []);

  const averageRating = reviews.length ? reviews.reduce((acc, review) => acc + review.rating, 0) / reviews.length : 0

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return "bg-green-500"
    if (rating >= 4.0) return "bg-lime-500"
    if (rating >= 3.0) return "bg-yellow-500"
    if (rating >= 2.0) return "bg-orange-500"
    return "bg-red-500"
  }

  const getRatingText = (rating) => {
    if (rating >= 4.5) return "EXCELLENT"
    if (rating >= 4.0) return "VERY GOOD"
    if (rating >= 3.0) return "GOOD"
    if (rating >= 2.0) return "FAIR"
    return "POOR"
  }

  const getTimeDifference = (timestamp) => {
    const now = new Date()
    const then = new Date(timestamp)
    const diffInSeconds = Math.floor((now - then) / 1000)
    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`
    if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`
    return `${Math.floor(diffInSeconds / 31536000)} years ago`
  }

  const toggleExpand = (reviewId) => {
    setExpandedReviews((prev) => ({
      ...prev,
      [reviewId]: !prev[reviewId],
    }))
  }

  // Updated sorting logic
  const sortedReviews = [...reviews].sort((a, b) => {
    // Convert createdAt to timestamps for comparison
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();

    // Ensure rating is a number
    const ratingA = Number(a.rating);
    const ratingB = Number(b.rating);

    switch (sortBy) {
      case "highest":
        // Sort by rating (descending), then by date (newest first)
        return ratingB - ratingA || dateB - dateA;
      case "lowest":
        // Sort by rating (ascending), then by date (newest first)
        return ratingA - ratingB || dateB - dateA;
      case "newest":
      default:
        // Sort by date (newest first)
        return dateB - dateA;
    }
  });

  return (
    <div className="bg-[#EDEFEF] min-h-screen pb-6">
      {/* Header */}
      <header className="bg-white p-4 sticky top-0 z-10 shadow-sm">
        <div className="flex items-center gap-2">
          <Button variant="ghost" className="p-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-xl font-bold">Reviews</h1>
        </div>
      </header>

      {/* Rating Summary */}
      <div className="bg-white p-4 mt-2 rounded-lg mx-2">
        <h2 className="text-lg font-bold mb-4">Ratings for {villaName}</h2>

        <div className="flex items-center gap-6 mb-4">
          <div className="text-center">
            <div
              className="text-4xl font-bold"
              style={{ color: getRatingColor(averageRating).replace("bg-", "text-") }}
            >
              {averageRating.toFixed(1)}
            </div>
            <div className="text-xs font-semibold">{getRatingText(averageRating)}</div>
            <div className="text-xs text-gray-500">{reviews.length} ratings</div>
          </div>

          <div className="flex-1">
            {[5, 4, 3, 2, 1].map((rating) => {
              const count = reviews.filter((r) => r.rating === rating).length
              const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0
              return (
                <div key={rating} className="flex items-center gap-2 mb-1">
                  <span className="w-3 text-xs">{rating}</span>
                  <Star className={`h-3 w-3 ${count > 0 ? "text-yellow-400" : "text-gray-400"}`} />
                  <div className="flex-1 bg-gray-200 rounded-full h-1.5 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-300 ${getRatingColor(rating)}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="w-8 text-xs text-gray-500">{percentage.toFixed(0)}%</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Sort Controls */}
      <div className="bg-white p-4 mt-2 rounded-lg mx-2 flex items-center justify-between">
        <span className="text-sm font-medium text-gray-600">Sort by:</span>
        <div className="flex gap-2">
          <Button
            variant={sortBy === "newest" ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy("newest")}
            className={`text-sm ${
              sortBy === "newest" 
                ? "bg-black text-white" 
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Newest First
          </Button>
          <Button
            variant={sortBy === "highest" ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy("highest")}
            className={`text-sm ${
              sortBy === "highest" 
                ? "bg-black text-white" 
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Highest Rating
          </Button>
          <Button
            variant={sortBy === "lowest" ? "default" : "outline"}
            size="sm"
            onClick={() => setSortBy("lowest")}
            className={`text-sm ${
              sortBy === "lowest" 
                ? "bg-black text-white" 
                : "text-gray-600 hover:bg-gray-50"
            }`}
          >
            Lowest Rating
          </Button>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-2 px-2 mt-2">
        {sortedReviews.map((review) => (
          <Card key={review.id} className="shadow-sm overflow-hidden">
            <CardContent className="p-4">
              <div className="flex gap-2 p-2 bg-white rounded-lg">
                {/* Avatar Section - Moved slightly to the left */}
                <div className="-ml-3">
                  <img
                    src={userPhotos[review.userId] || review.userPhoto || "/placeholder.svg"}
                    alt={userNames[review.userId] || review.userName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </div>

                {/* Content Section */}
                <div className="flex-1">
                  {/* Username */}
                  <p className="font-bold text-sm">
                    {userNames[review.userId] || review.userName}
                  </p>

                  {/* Stars and Timestamp */}
                  <div className="mb-1">
                    <div className="flex">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-3 w-3 ${i < review.rating ? "text-yellow-400 fill-current" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{getTimeDifference(review.createdAt)}</span>
                  </div>

                  {/* Review Comment */}
                  <div className={expandedReviews[review.id] ? "" : "line-clamp-2"}>
                    <p className="text-sm text-gray-700">{review.comment}</p>
                  </div>

                  {review.comment.length > 100 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs mt-1 p-0 h-auto text-blue-600"
                      onClick={() => toggleExpand(review.id)}
                    >
                      {expandedReviews[review.id] ? "Show less" : "Read more"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Replies Section */}
              {review.replies && Object.keys(review.replies).length > 0 && (
                <div className="mt-3 bg-gray-50 p-3 rounded-lg">
                  {Object.entries(review.replies).map(([replyId, reply]) => (
                    <div key={replyId} className="flex gap-2">
                      <img
                        src={reply.userPhoto || "/placeholder.svg"}
                        alt={reply.userName}
                        className="w-6 h-6 rounded-full"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs font-semibold">
                            {reply.userName}
                            {(isOwner || isAdmin) && <span className="text-blue-600 ml-1">(Owner)</span>}
                          </p>
                          <p className="text-xs text-gray-500">{getTimeDifference(reply.createdAt)}</p>
                        </div>
                        <p className="text-xs text-gray-700">{reply.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}